import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { formatDisplayDate } from "@/lib/dateFormat";
import AdminLayout from "@/components/AdminLayout";
import LetterBodyView from "@/components/LetterBodyView";
import { FileSignature, Download, Eye, CheckCircle2, Loader2 } from "lucide-react";

function isIssued(l) {
  return l?.approval_status === "Approved" || l?.approval_status === "Issued" || !l?.approval_status;
}

export default function MyLetters() {
  const [items, setItems] = useState([]);
  const [viewing, setViewing] = useState(null);
  const [acking, setAcking] = useState(false);

  const load = () => {
    api.get("/letters").then((r) => setItems(r.data || [])).catch(() => {});
  };

  useEffect(() => {
    load();
  }, []);

  // Safety: only show Issued / Approved letters on ESS
  const visible = useMemo(() => (items || []).filter(isIssued), [items]);

  const download = async (letter) => {
    try {
      const res = await api.get(`/exports/letter/${letter.id}.pdf`, { responseType: "blob" });
      const blob = new Blob([res.data]);
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = `${letter.type.replace(/ /g, "_")}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) {
      const d = e?.response?.data?.detail;
      alert(typeof d === "string" ? d : "Download not available until the letter is Issued");
    }
  };

  const acknowledge = async (letter) => {
    if (!window.confirm("Confirm that you have read and acknowledge this letter?")) return;
    setAcking(true);
    try {
      const r = await api.post(`/letters/${letter.id}/acknowledge`, { note: "" });
      setItems((arr) => arr.map((x) => (x.id === letter.id ? { ...x, ...r.data } : x)));
      if (viewing?.id === letter.id) setViewing({ ...viewing, ...r.data });
    } catch (e) {
      const d = e?.response?.data?.detail;
      alert(typeof d === "string" ? d : "Could not acknowledge letter");
    } finally {
      setAcking(false);
    }
  };

  const needsAck = (l) =>
    l.require_acknowledgement && l.acknowledgement_status !== "Acknowledged";

  const ackCell = (l) => {
    if (!l.require_acknowledgement && (l.acknowledgement_status || "NotRequired") === "NotRequired") {
      return <span className="text-xs text-slate-400">—</span>;
    }
    if (l.acknowledgement_status === "Acknowledged") {
      return (
        <span className="text-[11px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800">Acknowledged</span>
      );
    }
    return (
      <span className="text-[11px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">Action needed</span>
    );
  };

  return (
    <AdminLayout title="My Letters & Certificates" breadcrumbs={["Home", "Self Service", "Letters"]}>
      <div className="bg-white border border-slate-200 rounded shadow-sm">
        <div className="px-5 py-3.5 border-b border-slate-200">
          <h3 className="text-base font-semibold text-slate-800 flex items-center gap-2">
            <FileSignature className="h-5 w-5 text-[#226DB4]" /> Documents issued to me by HR
          </h3>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-3 font-semibold">Date</th>
              <th className="text-left px-4 py-3 font-semibold">Letter No.</th>
              <th className="text-left px-4 py-3 font-semibold">Type</th>
              <th className="text-left px-4 py-3 font-semibold">Title</th>
              <th className="text-left px-4 py-3 font-semibold">Issued By</th>
              <th className="text-left px-4 py-3 font-semibold">Status</th>
              <th className="text-right px-4 py-3 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-12 text-center text-slate-400" data-testid="my-letters-empty">
                  No letters have been issued to you yet
                </td>
              </tr>
            ) : (
              visible.map((l) => (
                <tr key={l.id} className="border-t border-slate-100 hover:bg-slate-50/60">
                  <td className="px-4 py-3 text-slate-700 font-medium">{formatDisplayDate(l.issue_date)}</td>
                  <td className="px-4 py-3 text-slate-700 font-mono text-xs">{l.letter_number || "—"}</td>
                  <td className="px-4 py-3 text-slate-700">{l.type}</td>
                  <td className="px-4 py-3 text-slate-800">{l.title}</td>
                  <td className="px-4 py-3 text-slate-600">{l.issued_by_name}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-col gap-1">
                      <span className="text-[11px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 w-fit">Issued</span>
                      {ackCell(l)}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1.5">
                      {needsAck(l) && (
                        <button
                          onClick={() => acknowledge(l)}
                          disabled={acking}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-[#089949] hover:bg-[#067A3A] text-white rounded disabled:opacity-60"
                          data-testid={`ack-my-letter-${l.id}`}
                        >
                          {acking ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
                          Acknowledge
                        </button>
                      )}
                      <button onClick={() => setViewing(l)} className="p-1.5 text-slate-500 hover:bg-slate-100 rounded" data-testid={`view-my-letter-${l.id}`}>
                        <Eye className="h-4 w-4" />
                      </button>
                      <button onClick={() => download(l)} className="p-1.5 text-[#226DB4] hover:bg-[#226DB4]/10 rounded" data-testid={`download-my-letter-${l.id}`}>
                        <Download className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {viewing && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[92vh] flex flex-col">
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-[#f9fafb]">
              <h3 className="text-lg font-semibold text-slate-800">{viewing.title}</h3>
              <button onClick={() => setViewing(null)} className="p-2 text-slate-400 hover:bg-slate-100 rounded">✕</button>
            </div>
            <div className="flex-1 overflow-y-auto p-8">
              <div className="text-sm text-slate-500 mb-4 space-y-1">
                <div>
                  Issued on <b>{formatDisplayDate(viewing.issue_date)}</b> by {viewing.issued_by_name}
                </div>
                {viewing.letter_number ? (
                  <div className="font-mono text-xs">Letter No: {viewing.letter_number}</div>
                ) : null}
                <div className="flex items-center gap-2">
                  <span className="text-[11px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800">Issued</span>
                  {ackCell(viewing)}
                </div>
              </div>
              <LetterBodyView body={viewing.body} letter={viewing} className="font-serif" />
            </div>
            <div className="px-6 py-4 border-t border-slate-200 bg-[#f9fafb] flex justify-end gap-2">
              {needsAck(viewing) && (
                <button
                  onClick={() => acknowledge(viewing)}
                  disabled={acking}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-[#089949] hover:bg-[#067A3A] text-white rounded font-medium disabled:opacity-60"
                >
                  {acking ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                  Acknowledge
                </button>
              )}
              <button onClick={() => download(viewing)} className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-[#226DB4] hover:bg-[#1B5A94] text-white rounded font-medium">
                <Download className="h-4 w-4" /> Download PDF
              </button>
              <button onClick={() => setViewing(null)} className="px-4 py-2 text-sm border border-slate-200 rounded">Close</button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
